import React from 'react';

import { useFormContext, useWatch } from 'react-hook-form';

import {
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from '@mui/material';

import ControlledTextField from '../../../components/ControlledTextField';
import ControlledPeoplePicker from '../../../components/ControlledPeoplePicker';
import ControlledAirportSelect from '../../../components/ControlledAirportSelect';

import {
  DispatchEventReport, Employee, EmployeePosition, EventReport, FormGenerator,
} from '../../../core/api/clients';
import { useDispatchEventFieldOptions } from '../../../core/context/DispatchEventFieldOptionsContext';
import { useDispatchEventReportData } from '../../../core/context/DispatchEventReportDataContext';
import ControlledAircraftSelect from '../../../components/ControlledAircraftSelect';

import { transformToUtcTime } from '../../../core/textTransforms';
import ControlledSelect from '../../../components/ControlledSelect';

function FlightStep({ visible, readOnly, isRevenue }:
{
  visible: boolean,
  readOnly?: boolean,
  isRevenue?: boolean,
}) {
  const { airports, aircrafts } = useDispatchEventFieldOptions();
  const { dispatchEventReport, allowedActions } = useDispatchEventReportData();
  const { control, setValue } = useFormContext<DispatchEventReport>();
  const [isOnBehalfOfUsed, setIsOnBehalfOfUsed] = React.useState<boolean>(false);
  const onBehalfOfFields = useWatch<DispatchEventReport>({ name: ['onBehalfOf', 'onBehalfOfId', 'onBehalfOfDomicile'] });
  React.useEffect(() => {
    setIsOnBehalfOfUsed(onBehalfOfFields.every((value) => value));
  }, [onBehalfOfFields]);

  const handleIsOnBehalfOfUsedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isOnBehalfOfUsed) {
      setValue('onBehalfOf', null, { shouldDirty: true, shouldTouch: true });
      setValue('onBehalfOfId', null, { shouldDirty: true, shouldTouch: true });
      setValue('onBehalfOfDomicile', null, { shouldDirty: true, shouldTouch: true });
    }

    setIsOnBehalfOfUsed(event.target.checked);
  };

  return (
    <Collapse in={visible}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <ControlledTextField<DispatchEventReport>
            name="flightNumber"
            control={control}
            rules={{ required: dispatchEventReport.formGeneratedFrom !== FormGenerator.Blank }}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ControlledSelect<DispatchEventReport>
            name="mainlinePartner"
            control={control}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
            menuItems={[
              { name: 'AA', value: 'AA' },
              { name: 'ZW', value: 'ZW' },
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ControlledAircraftSelect<DispatchEventReport>
            name="aircraftNumber"
            control={control}
            rules={{ required: true }}
            readOnly={readOnly}
            options={aircrafts ?? []}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledAirportSelect<DispatchEventReport>
            name="departureAirport"
            control={control}
            rules={{ required: true }}
            readOnly={readOnly}
            options={airports ?? []}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledAirportSelect<DispatchEventReport>
            name="scheduledArrivalAirport"
            control={control}
            rules={{ required: true }}
            readOnly={readOnly}
            options={airports ?? []}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledAirportSelect<DispatchEventReport>
            name="landingAirport"
            control={control}
            rules={{ required: true }}
            readOnly={readOnly}
            options={airports ?? []}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        {dispatchEventReport.pushTime && (
          <React.Fragment>
            <Grid item xs={3} textAlign="center">
              <Typography variant="caption">OUT: </Typography>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{transformToUtcTime(dispatchEventReport.pushTime)}</Typography>
            </Grid>
            <Grid item xs={3} textAlign="center">
              <Typography variant="caption">OFF: </Typography>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{transformToUtcTime(dispatchEventReport.offTime)}</Typography>
            </Grid>
            <Grid item xs={3} textAlign="center">
              <Typography variant="caption">ON: </Typography>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{transformToUtcTime(dispatchEventReport.onTime)}</Typography>
            </Grid>
            <Grid item xs={3} textAlign="center">
              <Typography variant="caption">IN: </Typography>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{transformToUtcTime(dispatchEventReport.inTime)}</Typography>
            </Grid>
          </React.Fragment>
        )}
      </Grid>
      <Divider sx={{ my: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ControlledPeoplePicker<DispatchEventReport>
            groupName="captain"
            name="captain"
            position={EmployeePosition.Flight}
            control={control}
            fieldNames={['captainId', 'captain', 'captainDomicile']}
            onPersonSelected={(employee: Employee | null) => {
              setValue('captain', employee?.name ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('captainId', employee?.id ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('captainDomicile', employee?.location ?? null, { shouldDirty: true, shouldTouch: true });
            }}
            readOnly={readOnly}
            rules={{ required: dispatchEventReport.formGeneratedFrom !== FormGenerator.Blank }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12}>
          <ControlledPeoplePicker<DispatchEventReport>
            groupName="firstOfficer"
            name="firstOfficer"
            position={EmployeePosition.Flight}
            control={control}
            fieldNames={['firstOfficerId', 'firstOfficer', 'firstOfficerDomicile']}
            onPersonSelected={(employee: Employee | null) => {
              setValue('firstOfficer', employee?.name ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('firstOfficerId', employee?.id ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('firstOfficerDomicile', employee?.location ?? null, { shouldDirty: true, shouldTouch: true });
            }}
            readOnly={readOnly}
            rules={{ required: dispatchEventReport.formGeneratedFrom !== FormGenerator.Blank }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12}>
          <ControlledPeoplePicker<DispatchEventReport>
            groupName="flightAttendant"
            name="flightAttendant"
            position={EmployeePosition.InFlight}
            control={control}
            fieldNames={['flightAttendantId', 'flightAttendant', 'flightAttendantDomicile']}
            onPersonSelected={(employee: Employee | null) => {
              setValue('flightAttendant', employee?.name ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('flightAttendantId', employee?.id ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('flightAttendantDomicile', employee?.location ?? null, { shouldDirty: true, shouldTouch: true });
            }}
            readOnly={readOnly}
            rules={{ required: isRevenue && dispatchEventReport.formGeneratedFrom !== FormGenerator.Blank }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
      </Grid>
      <Divider sx={{ my: 4 }} />
      <Grid container spacing={2}>
        <Grid item alignItems="center" justifyContent="center" xs={12}>
          <FormControlLabel
            control={(
              <Switch
                checked={isOnBehalfOfUsed}
                onChange={handleIsOnBehalfOfUsedChange}
                name="IsOnBehalfOfUsed"
              />
            )}
            label="I am submitting this form on behalf of another person"
          />
        </Grid>
        {isOnBehalfOfUsed && (
          <Grid item xs={12}>
            <ControlledPeoplePicker<DispatchEventReport>
              groupName="onBehalfOf"
              name="onBehalfOf"
              position={EmployeePosition.Dispatch}
              fieldNames={['onBehalfOfId', 'onBehalfOf', 'onBehalfOfDomicile']}
              onPersonSelected={(employee: Employee | null) => {
                setValue('onBehalfOf', employee?.name ?? null, { shouldDirty: true, shouldTouch: true });
                setValue('onBehalfOfId', employee?.id ?? null, { shouldDirty: true, shouldTouch: true });
                setValue('onBehalfOfDomicile', employee?.location ?? null, { shouldDirty: true, shouldTouch: true });
              }}
              readOnly={readOnly}
              disableEditPopper={!allowedActions?.edit}
            />
          </Grid>
        )}
      </Grid>
    </Collapse>
  );
}

export default FlightStep;
